import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { FilterIcon, TrashIcon, PlusIcon } from '@heroicons/react/outline';
import { debounce } from 'lodash';
import InputGroup from '../../../../components/InputGroup';
import IconButton from '../../../../components/IconButton';
import SearchIcon from '../../../../svgs/Search';
import CloseX from '../../../../svgs/CloseX';
import {
  useCalendarViewStore,
  SavedFilterPreset,
} from '../stores/useCalendarViewStore';
import { customToast } from '../../../../components/ToastAlert/customToast';

const TITLE_FILTER_DEBOUNCE_MS = 400;

export const CalendarFilterMenu: React.FC = () => {
  const titleFilter = useCalendarViewStore((s) => s.titleFilter);
  const setTitleFilter = useCalendarViewStore((s) => s.setTitleFilter);
  const savedFilterPresets = useCalendarViewStore((s) => s.savedFilterPresets);
  const saveFilterPreset = useCalendarViewStore((s) => s.saveFilterPreset);
  const applyFilterPreset = useCalendarViewStore((s) => s.applyFilterPreset);
  const deleteFilterPreset = useCalendarViewStore((s) => s.deleteFilterPreset);

  // Local input value lets us debounce writes to the store (and therefore
  // the GraphQL query) while still feeling responsive while typing.
  const [localValue, setLocalValue] = useState<string>(titleFilter ?? '');

  // Sync local state when the store value changes from elsewhere (preset
  // apply, reset, etc.) without clobbering active typing.
  useEffect(() => {
    setLocalValue(titleFilter ?? '');
  }, [titleFilter]);

  const debouncedSetTitleFilter = useMemo(
    () =>
      debounce((value: string) => {
        setTitleFilter(value);
      }, TITLE_FILTER_DEBOUNCE_MS),
    [setTitleFilter],
  );

  useEffect(() => {
    return () => {
      debouncedSetTitleFilter.cancel();
    };
  }, [debouncedSetTitleFilter]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setLocalValue(next);
    debouncedSetTitleFilter(next);
  };

  const handleClear = () => {
    setLocalValue('');
    debouncedSetTitleFilter.cancel();
    setTitleFilter(null);
  };

  const isActive = Boolean(titleFilter);
  const trimmedCurrent = (titleFilter ?? '').trim();
  const currentMatchesExistingPreset = useMemo(
    () =>
      savedFilterPresets.some(
        (p) => p.value.trim().toLowerCase() === trimmedCurrent.toLowerCase(),
      ),
    [savedFilterPresets, trimmedCurrent],
  );
  const canSaveCurrent = isActive && !currentMatchesExistingPreset;

  const handleSaveCurrent = () => {
    const preset = saveFilterPreset(trimmedCurrent);
    if (preset) {
      customToast.success(`Saved filter "${preset.value}"`);
    }
  };

  const handleApply = (preset: SavedFilterPreset) => {
    // Cancel any pending debounced write so an in-progress typed value
    // doesn't clobber the preset ~400ms later.
    debouncedSetTitleFilter.cancel();
    applyFilterPreset(preset.id);
  };

  const handleApplyKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    preset: SavedFilterPreset,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleApply(preset);
    }
  };

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    preset: SavedFilterPreset,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    deleteFilterPreset(preset.id);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center space-x-1">
      <div className="w-[240px]">
        <InputGroup
          ref={inputRef}
          inputSize="extra-small"
          placeholder="Filter events by title…"
          IconLeft={SearchIcon}
          iconLeftClassName="h-4 w-4 text-neutral-125"
          IconRight={localValue ? CloseX : null}
          iconRightClassName="h-3 w-3 text-neutral-125"
          iconRightOnClick={localValue ? handleClear : undefined}
          highlighted={isActive}
          value={localValue}
          onChange={handleChange}
          aria-label="Filter calendar events by title"
          className="!rounded-md !pl-9 !pr-8"
        />
      </div>

      <Menu as="div" className="relative">
        <Menu.Button as={Fragment}>
          <IconButton
            aria-label="Saved filter presets"
            IconComponent={FilterIcon}
            iconClassName={`h-5 w-5 min-w-[20px] min-h-[20px] ${
              isActive ? 'text-[#FFA41C]' : 'text-neutral-125'
            } hover:text-green-150`}
          />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-20 mt-2 w-72 origin-top-right divide-y divide-neutral-75 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="px-3 pt-2 pb-1">
              <p className="text-small-caption font-medium uppercase tracking-wide text-neutral-125">
                Saved filters
              </p>
            </div>

            <div className="max-h-64 overflow-auto py-1">
              {savedFilterPresets.length === 0 ? (
                <p className="px-3 py-2 text-body text-neutral-125">
                  No saved filters yet.
                </p>
              ) : (
                savedFilterPresets.map((preset) => (
                  <Menu.Item key={preset.id}>
                    {({ active }) => (
                      // Outer is a div (not a button) because it contains
                      // another interactive element (the trash button), and
                      // nested interactive elements are invalid HTML. Headless
                      // UI's Menu handles keyboard nav and active state; we
                      // add role+tabIndex+keyDown so this row remains
                      // keyboard-actionable.
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleApply(preset)}
                        onKeyDown={(e) => handleApplyKeyDown(e, preset)}
                        className={`flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left ${
                          active ? 'bg-green-50' : ''
                        }`}
                      >
                        <span className="block min-w-0 flex-1 truncate pr-2 text-body text-neutral-150">
                          {preset.value}
                        </span>
                        <button
                          type="button"
                          aria-label={`Delete saved filter "${preset.value}"`}
                          onClick={(e) => handleDelete(e, preset)}
                          className="rounded p-1 text-neutral-125 hover:bg-neutral-50 hover:text-red-100"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </Menu.Item>
                ))
              )}
            </div>

            <div className="py-1">
              <Menu.Item disabled={!canSaveCurrent}>
                {({ active, disabled }) => (
                  <button
                    type="button"
                    onClick={handleSaveCurrent}
                    disabled={disabled}
                    className={`flex w-full items-center px-3 py-2 text-left text-body ${
                      disabled
                        ? 'cursor-not-allowed text-neutral-125/60'
                        : active
                        ? 'bg-green-50 text-green-150'
                        : 'text-green-150'
                    }`}
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    {isActive
                      ? currentMatchesExistingPreset
                        ? 'Already saved'
                        : `Save "${trimmedCurrent}" as preset`
                      : 'Type a filter to save'}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default CalendarFilterMenu;
